import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, X, Plus, Minus, Check, Loader2, Truck, Clock, Leaf, Apple, Croissant, Fish, Egg, Coffee, ArrowRight, Tag } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";
import { shopProducts, shopCheckout, shopOrderStatus } from "@/lib/api";
import { toast } from "sonner";

const GREEN = "#2F8F3E";
const DARK = "#1F3322";
const CREAM = "#F4F8F1";
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const CART_KEY = "fresh-cart";
const money = (c) => `$${(c / 100).toFixed(2)}`;
const CATS = [
  { icon: Apple, t: "Fresh Produce" }, { icon: Croissant, t: "Bakery" }, { icon: Fish, t: "Meat & Seafood" },
  { icon: Egg, t: "Dairy & Eggs" }, { icon: Coffee, t: "Pantry" }, { icon: Leaf, t: "Frozen" },
];

export default function FreshMarketDemo() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; } });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => { window.scrollTo(0, 0); document.title = "Fresh Market (Demo) | WebsiteTech.pro"; shopProducts("fresh").then((d) => setProducts(d.products)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("paid") === "1" && sid) {
      let n = 0; const poll = async () => {
        try { const d = await shopOrderStatus(sid); if (d.payment_status === "paid") { setDone(true); setCart([]); localStorage.removeItem(CART_KEY); setParams({}, { replace: true }); return; } } catch { /* */ }
        if (n++ < 15) setTimeout(poll, 1500); else { setCart([]); localStorage.removeItem(CART_KEY); setParams({}, { replace: true }); toast.success("Order received — thank you!"); }
      }; poll();
    } else if (params.get("canceled") === "1") { toast("Checkout canceled — your cart is saved."); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const add = useCallback((p) => {
    setCart((c) => { const f = c.find((i) => i.id === p.id); return f ? c.map((i) => i.id === p.id ? { ...i, qty: i.qty + 1 } : i) : [...c, { id: p.id, name: p.name, price: p.price, image: p.image, qty: 1 }]; });
    setOpen(true); toast.success(`${p.name} added`);
  }, []);
  const setQty = (id, d) => setCart((c) => c.flatMap((i) => i.id === id ? (i.qty + d <= 0 ? [] : [{ ...i, qty: i.qty + d }]) : [i]));
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cart.reduce((s, i) => s + i.qty, 0);
  const checkout = async () => {
    if (!cart.length) return; setBusy(true);
    try { const r = await shopCheckout({ store: "fresh", items: cart.map((i) => ({ id: i.id, qty: i.qty })), origin_url: window.location.origin }); window.location.href = r.checkout_url; }
    catch { toast.error("Could not start checkout."); setBusy(false); }
  };
  const deals = products.filter((p) => p.deal);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: DARK, ...body }} data-testid="grocery-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: GREEN }}>WebsiteTech.pro Demo · Grocery — view all demos</Link>
      <div className="text-center text-xs py-2 text-white flex items-center justify-center gap-2" style={{ background: "#E2703A" }}><Tag size={12} /> Daily Deals — save up to 40% on fresh picks</div>
      <DemoTopNav testid="grocery-nav"
        logo={<div className="flex items-center gap-2"><Leaf size={22} style={{ color: GREEN }} /><div className="text-xl font-extrabold tracking-tight" style={{ color: GREEN }}>Fresh Market</div></div>}
        items={["Shop", "Deals", "Categories", "Recipes", "Delivery"]} ctaLabel={count ? `Cart · ${count}` : "Cart"} onCta={() => setOpen(true)}
        theme={{ bg: "rgba(244,248,241,0.94)", text: DARK, accent: GREEN, accentText: "#fff", border: "#dde8d8" }} />

      <header data-testid="grocery-hero" className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-extrabold">Fresh Food.<br /><span style={{ color: GREEN }}>Better Prices.</span></h1>
          <p className="mt-5 text-lg opacity-75 max-w-md">Quality groceries for healthier, happier tomorrows — with same-day pickup and local delivery.</p>
          <div className="mt-7 flex gap-3">
            <a href="#shop" className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center gap-2" style={{ background: GREEN }}>Shop Now <ArrowRight size={14} /></a>
            <a href="#shop" className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold" style={{ border: `1px solid ${GREEN}66`, color: DARK }}>Today's Deals</a>
          </div>
          <div className="mt-6 flex gap-5 text-sm opacity-70">
            <span className="flex items-center gap-1.5"><Truck size={15} style={{ color: GREEN }} /> Local delivery</span>
            <span className="flex items-center gap-1.5"><Clock size={15} style={{ color: GREEN }} /> Same-day pickup</span>
          </div>
        </motion.div>
        <motion.img initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} src="https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&w=900&q=80&ixlib=rb-4.1.0" alt="" className="rounded-2xl h-[340px] w-full object-cover shadow-lg" />
      </header>

      {/* categories */}
      <section className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-3 md:grid-cols-6 gap-3">
        {CATS.map((c) => (<div key={c.t} className="rounded-xl bg-white border p-4 flex flex-col items-center gap-2 text-center" style={{ borderColor: "#dde8d8" }}><c.icon size={22} style={{ color: GREEN }} /><span className="text-xs font-medium leading-tight">{c.t}</span></div>))}
      </section>

      {/* deals */}
      {deals.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8">
          <h2 className="text-2xl font-extrabold mb-5 flex items-center gap-2"><Tag size={20} style={{ color: "#E2703A" }} /> Today's Deals</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="grocery-deals">
            {deals.map((p) => <ProductCard key={p.id} p={p} onAdd={add} />)}
          </div>
        </section>
      )}

      {/* all products */}
      <section id="shop" data-testid="grocery-products" className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-extrabold mb-5">Shop Groceries</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((p) => <ProductCard key={p.id} p={p} onAdd={add} />)}
        </div>
      </section>

      <section className="py-14 text-center" style={{ background: "#E7F0E3" }}>
        <p className="max-w-2xl mx-auto px-6 text-2xl md:text-3xl leading-relaxed font-semibold">More than groceries — a brighter neighborhood.</p>
        <p className="mt-2 text-sm opacity-60">Locally sourced when we can. Always fresh, always fair.</p>
      </section>

      <footer data-testid="grocery-footer" className="text-white" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2"><Leaf size={18} style={{ color: "#9BD59B" }} /><span className="font-extrabold">Fresh Market</span></div>
          <p className="opacity-70">Shop · Deals · Categories · Recipes · Delivery</p>
          <p className="opacity-70">Fresh food. Better prices.</p>
        </div>
      </footer>

      {/* cart */}
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[80]" data-testid="grocery-cart-drawer">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 260 }} className="absolute right-0 top-0 h-full w-full sm:w-[400px] bg-white flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#eee" }}><span className="font-bold">Your Cart {count ? `(${count})` : ""}</span><button onClick={() => setOpen(false)} data-testid="grocery-cart-close"><X size={18} /></button></div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {cart.length === 0 ? <div className="text-center text-slate-400 py-20 text-sm" data-testid="grocery-cart-empty"><ShoppingCart className="mx-auto mb-3" size={28} />Your cart is empty.</div> : cart.map((i) => (
                  <div key={i.id} className="flex gap-3 items-center" data-testid="grocery-cart-item">
                    <img src={i.image} alt="" className="h-14 w-14 rounded object-cover" />
                    <div className="flex-1"><div className="text-sm font-medium">{i.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <button onClick={() => setQty(i.id, -1)} data-testid={`grocery-qty-dec-${i.id}`} className="h-6 w-6 grid place-items-center rounded border" style={{ borderColor: "#ddd" }}><Minus size={12} /></button>
                        <span className="text-sm w-5 text-center" data-testid={`grocery-qty-${i.id}`}>{i.qty}</span>
                        <button onClick={() => setQty(i.id, 1)} data-testid={`grocery-qty-inc-${i.id}`} className="h-6 w-6 grid place-items-center rounded border" style={{ borderColor: "#ddd" }}><Plus size={12} /></button>
                      </div>
                    </div>
                    <div className="text-sm" style={{ color: GREEN }}>{money(i.price * i.qty)}</div>
                  </div>
                ))}
              </div>
              {cart.length > 0 && (
                <div className="border-t p-5 space-y-3" style={{ borderColor: "#eee" }}>
                  <div className="flex justify-between text-sm"><span className="opacity-60">Subtotal</span><span className="font-semibold">{money(subtotal)}</span></div>
                  <button onClick={checkout} disabled={busy} data-testid="grocery-checkout-btn" className="w-full rounded-full py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: GREEN }}>{busy ? <Loader2 size={15} className="animate-spin" /> : null} Checkout · {money(subtotal)}</button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {done && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setDone(false)} data-testid="grocery-order-success">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-8 max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full mx-auto grid place-items-center" style={{ background: `${GREEN}22`, color: GREEN }}><Check size={26} /></div>
            <h3 className="mt-5 text-2xl font-extrabold">Order placed!</h3>
            <p className="mt-2 text-sm text-slate-500">Thanks for shopping Fresh Market. We'll have your groceries ready for pickup or delivery shortly.</p>
            <button onClick={() => setDone(false)} className="mt-6 rounded-full px-8 py-3 text-xs tracking-widest uppercase font-semibold text-white" style={{ background: GREEN }}>Keep Shopping</button>
          </motion.div>
        </div>
      )}

      <AIConciergeWidget demo="grocery" concierge={DEMO_THEMES.grocery.concierge} />
    </div>
  );
}

function ProductCard({ p, onAdd }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden border" style={{ borderColor: "#dde8d8" }} data-testid={`grocery-product-${p.id}`}>
      <div className="relative h-32 overflow-hidden">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&w=400&q=80&ixlib=rb-4.1.0"; }} />
        {p.deal && <span className="absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white" style={{ background: "#E2703A" }}>Deal</span>}
      </div>
      <div className="p-3">
        <div className="text-sm font-medium leading-tight">{p.name}</div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: GREEN }}>{money(p.price)}</span>
          {p.was && <span className="text-xs text-slate-400 line-through">{money(p.was)}</span>}
        </div>
        <button onClick={() => onAdd(p)} data-testid={`grocery-add-${p.id}`} className="mt-2 w-full rounded-full py-2 text-xs tracking-widest uppercase font-semibold text-white" style={{ background: GREEN }}>Add</button>
      </div>
    </div>
  );
}
